/**
 * 전장 러너 (일감 S1) — 전투 "실행"과 벽돌 그리드 빌드를 소유하는 셸 모듈.
 *
 * GameScene에서 추출: 발사 루프(fire/fireOnce)·충돌 처리(handleHit)·피해/폭발/연쇄/분열 실행·
 * 챕터/멜로디/던전 그리드 빌드·그룹(bricks/projectiles)·발사 타이머. "무엇을/얼마나"의
 * 결정은 CombatSystem(G1, Phaser-free)이, 여기는 스폰·FX·물리·트윈만 (패러다임 규칙 1·6).
 *
 * 규약: 컨트롤러는 항상 ctx getter 경유(applySaveData 인스턴스 교체 stale 방지), 타이머/트윈은
 * scene 소속(셧다운 자동 정리), scene.restart 시 러너는 create()에서 통째 재생성 + attach().
 * handleHit(화살표 프로퍼티)의 정의와 physics 등록(attach)을 같은 파일에 둔다 — 바인딩 함정 차단.
 */
import Phaser from "phaser";
import type { RectMask } from "@yjsgames/phaser-ui";
import { muzzleFlash } from "@yjsgames/phaser-fx";
import {
  COMBAT,
  BATTLEFIELD_PRESSURE,
  GRID,
} from "../data/balance.js";
import { CombatSystem, type CombatFlowSpecResult } from "../systems/CombatSystem.js";
import { AnalyticsEvent } from "../analytics/analyticsEvents.js";
import { MelodySequencer } from "../systems/MelodySequencer.js";
import { aimBaseAngleRad } from "../systems/aimAngle.js";
import {
  filterAimCandidatesAboveOrigin,
  selectAimTarget,
  type AimCandidate,
} from "../systems/aimTargetSelect.js";
import { toggleTarget } from "../systems/userTarget.js";
import { Brick } from "../objects/Brick.js";
import { AimTargetMarker } from "../objects/AimTargetMarker.js";
import {
  type NearestBrickLocator,
  type Projectile,
} from "../objects/Projectile.js";
import { ProjectileFactory } from "../objects/ProjectileFactory.js";
import { findNearestPoint, collectAimCandidates } from "./battlefieldTargeting.js";
import type { ProjectileSpec } from "../models/ProjectileSpec.js";
import {
  scenePlayfieldBounds,
  sceneLauncherY,
} from "../ui/sceneLayout.js";
import type { GameContext } from "../app/GameContext.js";
import { BattlefieldRuntimePools } from "./battlefieldRuntimePools.js";
import { updateBattlefieldFrame } from "./battlefieldFrameRuntime.js";
import { BattlefieldGridRuntime } from "./battlefieldGridRuntime.js";
import { BattlefieldImpactRuntime } from "./battlefieldImpactRuntime.js";
import type { BattleProcEvent } from "../ui/ModuleGridView.js";
import { hasBrickReachedLauncher, pressureBrickBottom, resolveBrickDropDistance, resolveContinuousPressureDistance, resolveLowestPressureDangerBottom, resolvePressureProgress, resolvePressureThreat, type PressureProgressState, type PressureThreat } from "./battlefieldPressure.js";
import { FORCE_LOW_SPEC_RENDER_ENABLED } from "../env/appFlavor.js";
import { spaceshipById } from "../data/spaceshipData.js";
import { pickSpaceshipMuzzleFlash } from "../systems/spaceshipMuzzleFlash.js";
import { resolveSpaceshipProjectileCount, spaceshipMuzzlesForFlow } from "../systems/spaceshipProjectilePattern.js";

const AIM_TARGET_SAMPLE_MS = 80;
const TARGET_MARKER_SAMPLE_MS = 80;

export interface BattlefieldDeps {
  scene: Phaser.Scene;
  ctx: GameContext;
  combat: CombatSystem;
  /** 게임뷰 마스크 — buildUi에서 생성되므로 getter (생성 전 발사분은 미렌더 구간이라 무해). */
  gameviewMask: () => RectMask | undefined;
  /** 발동 연출 — 발동한 instanceId 목록 + 시작 파이프 발동 여부(startFired)를 전투탭에 표시. */
  onProc: (procs: readonly BattleProcEvent[], startFired: boolean) => void;
  /** 활성 벽돌 전멸 — 분기(던전 클리어 vs 다음 챕터)는 호출자(GameScene)가 결정. */
  onFieldCleared: () => void;
  /** 벽돌이 발사대에 닿아 일반 전투 패배가 확정됨. */
  onGameOver: () => void;
  /** 적 그래픽의 bottom이 닿으면 패배하는 시각 기준선. 미주입 시 발사축 y를 fallback으로 쓴다. */
  defeatDeadlineY?: () => number;
  /** 던전 등 별도 실패 조건이 있는 모드에서는 일반 전투 압박을 끈다. */
  isDefeatPressureEnabled?: () => boolean;
  /** 미스터리 블록 다이아·유물(비가역) 지급 시 즉시 저장 — PersistScheduler.flushNow 경유(debounce 우회). */
  persistNow: () => void;
  /** 보상 fly-to 타겟 좌표 + 아이콘 키 — 미스터리 골드/다이아/유물 획득 연출(프레임 HUD 칩/유물 탭). 미존재면 null. */
  rewardTargetPos: (kind: "gold" | "diamond" | "artifact") => { x: number; y: number; iconKey: string } | null;
  /** 전투 골드 획득 배율 치트(세션 한정). 미주입 시 1배. */
  goldGainMultiplier?: () => number;
  /** @deprecated 시너지는 발견 보상이 아니라 강화 대상이다. 기존 boot 계약 호환용으로만 남긴다. */
  onSynergyDiscovered: (synergyId: string) => void;
}

export class BattlefieldRunner {
  private readonly melodySequencer = new MelodySequencer();
  private readonly factory = new ProjectileFactory();
  private runtime!: BattlefieldRuntimePools;
  private gridRuntime!: BattlefieldGridRuntime;
  private impactRuntime!: BattlefieldImpactRuntime;
  /** 발사 루프 타이머 — fireRate 스탯/유물 변경 시 delay 재설정을 위해 보관. */
  private fireTimer!: Phaser.Time.TimerEvent;
  private fireTimers: Phaser.Time.TimerEvent[] = [];
  /** 일반 전투 시간 압박 타이머 — 일정 주기마다 벽돌을 한 줄씩 내린다. */
  private pressureTimer!: Phaser.Time.TimerEvent;

  /** 현재 조준 중인 벽돌 식별자(gridX,gridY) — hysteresis로 등거리 떨림을 막는 sticky 상태(aimLocator 전용). */
  private aimTargetId: string | null = null;
  private lastAimSampleAt = Number.NEGATIVE_INFINITY;
  private lastAimOriginX = Number.NaN;
  private lastAimOriginY = Number.NaN;
  private cachedAimTarget: { x: number; y: number } | null = null;

  /** 사용자가 클릭으로 수동 지정한 강제 조준 대상(gridX,gridY). 그 블럭이 파괴되면 null로 리셋되어 자동 조준 복귀. */
  private userTargetId: string | null = null;

  /** 조준 타겟 마커 — 발사대가 조준 중인 블럭 위 시각 표시(시각·tween·마스크는 AimTargetMarker에 캡슐화). attach()에서 생성. */
  private aimMarker?: AimTargetMarker;
  private lastTargetMarkerUpdateAt = Number.NEGATIVE_INFINITY;
  /** 광고/앱 정책에 따른 전투 진행 정지. Phaser UI는 멈추지 않고 발사/물리/충돌만 정지한다. */
  private paused = false;
  /** 패배 처리 중 중복 트리거 방지. buildChapterGrid에서 다음 판 상태로 해제된다. */
  private gameOverTriggered = false;

  /** 미스터리 100% 강제 스폰 플래그(디버그 치트) — ON이면 buildChapterGrid가 모든 normal 셀을 mystery로 마킹. 세션 한정(영속 X). */
  private forceMysteryBricks = false;

  /** 특수 벽돌(방어막/장갑/재생) 강제 스폰 플래그(디버그 치트) — ON이면 챕터 무관 높은 확률로 마킹(QA 검증용). 세션 한정. */
  private forceSpecialBricks = false;

  /** 유도(homing)·분열 발사체 공용 locator — stateless 최근접. 발사체가 단일 조준 상태를 오염시키지 않게 분리. */
  readonly nearestBrick: NearestBrickLocator = (x, y) => this.findNearestBrick(x, y);

  /**
   * 포신·조준선·발사 순간 조준 전용 locator — hysteresis 적용. 현재 타겟(aimTargetId)을 기억하고 새 후보가
   * COMBAT.aimStickyMarginPx 이상 더 가까울 때만 전환한다(두 벽돌 등거리 시 매 프레임 A↔B 떨림 방지). 단일 런처 = 단일 상태.
   */
  readonly aimLocator: NearestBrickLocator = (x, y) => {
    const nowMs = this.deps.scene.time.now;
    if (
      nowMs - this.lastAimSampleAt < AIM_TARGET_SAMPLE_MS &&
      Math.abs(x - this.lastAimOriginX) < 0.5 &&
      Math.abs(y - this.lastAimOriginY) < 0.5
    ) {
      return this.cachedAimTarget;
    }
    this.lastAimSampleAt = nowMs;
    this.lastAimOriginX = x;
    this.lastAimOriginY = y;
    const candidates = filterAimCandidatesAboveOrigin(this.collectAimCandidates(), y);
    // 사용자 수동 타겟이 더 이상 후보에 없으면(파괴) 해제 — 다음 선택부터 가장 가까운 순으로 자동 복귀.
    if (this.userTargetId !== null && !candidates.some((c) => c.id === this.userTargetId)) {
      this.userTargetId = null;
    }
    const picked = selectAimTarget(
      x,
      y,
      candidates,
      this.aimTargetId,
      COMBAT.aimStickyMarginPx,
      this.userTargetId,
    );
    this.aimTargetId = picked?.id ?? null;
    this.cachedAimTarget = picked ? { x: picked.x, y: picked.y } : null;
    return this.cachedAimTarget;
  };

  constructor(private readonly deps: BattlefieldDeps) {}

  private get scene(): Phaser.Scene {
    return this.deps.scene;
  }

  /** 그룹 생성 + 충돌 등록 + 발사 타이머 시작 — create()에서 러너 생성 직후 1회. */
  attach(): void {
    this.runtime = new BattlefieldRuntimePools(
      this.scene,
      this.deps.gameviewMask,
      (brick) => this.toggleUserTarget(brick),
    );
    this.gridRuntime = new BattlefieldGridRuntime(
      this.scene,
      () => this.runtime,
      this.melodySequencer,
      this.deps.gameviewMask,
      () => this.selectedSpaceship()?.enemyScale ?? 1,
    );
    this.impactRuntime = new BattlefieldImpactRuntime({
      scene: this.scene,
      ctx: this.deps.ctx,
      combat: this.deps.combat,
      runtime: () => this.runtime,
      nearestBrick: this.nearestBrick,
      playfieldBounds: () => this.playfieldBounds(),
      melodySequencer: this.melodySequencer,
      onFieldCleared: this.deps.onFieldCleared,
      persistNow: this.deps.persistNow,
      rewardTargetPos: this.deps.rewardTargetPos,
      goldGainMultiplier: this.deps.goldGainMultiplier,
      forceMysteryBricks: () => this.forceMysteryBricks,
      gameviewMask: this.deps.gameviewMask,
    });
    this.runtime.attachCollision(this.impactRuntime.handleHit);
    // 조준 타겟 마커 — 시각/락온 tween은 AimTargetMarker에 캡슐화. 마스크는 첫 표시 시 콜백으로 1회 적용.
    if (!FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.aimMarker = new AimTargetMarker(this.scene, (img) => this.deps.gameviewMask()?.apply(img));
    }
    this.resetFireTimer();
    this.pressureTimer = this.scene.time.addEvent({
      delay: BATTLEFIELD_PRESSURE.dropIntervalMs,
      loop: true,
      callback: () => this.applyDefeatPressure(),
    });
    this.pressureTimer.paused = this.paused;
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    const fireTimers = this.fireTimers.length > 0 ? this.fireTimers : this.fireTimer ? [this.fireTimer] : [];
    for (const timer of fireTimers) timer.paused = paused;
    if (this.pressureTimer) this.pressureTimer.paused = paused;
  }

  /**
   * 매 프레임 연속 충돌(터널링 방지) — GameScene.update가 physics step 이후 호출한다.
   * 발사체가 한 프레임에 임계(ballRadius×2) 이상 이동하면(고속 speedMultiplier 누적 / delta spike)
   * physics.add.overlap(현재 위치 1점)이 벽돌을 건너뛸 수 있어, 직전→현재 위치 선분이 벽돌 AABB를
   * 관통하는지 보완 검사한다. 검출 시 overlap과 동일한 handleHit를 재사용(hitSet이 중복 타격 자연 차단).
   * bounce/homing은 velocity만 바꾸고 sweep은 위치 선분만 보므로 직교 — 거동 변화 없음.
   */
  update(aimAlreadyResolved = false): void {
    this.applyContinuousDefeatPressure(this.scene.game.loop.delta);
    updateBattlefieldFrame({
      scene: this.scene,
      runtime: this.runtime,
      paused: this.paused,
      handleHit: this.impactRuntime.handleHit,
      updateTargetMarker: () => this.updateTargetMarker(aimAlreadyResolved),
    });
  }

  /** 발사 루프 타이머 재설정 — fireRate 스탯/유물 변경 시 현재 간격을 즉시 반영. */
  resetFireTimer(): void {
    for (const timer of this.fireTimers) timer.remove(false);
    this.fireTimers = this.deps.combat.buildCombatFlowSpecs().map((flow) => {
      const timer = this.scene.time.addEvent({
        delay: flow.fireIntervalMs,
        loop: true,
        callback: () => this.fireFlow(flow.flowIndex),
      });
      timer.paused = this.paused;
      return timer;
    });
    this.fireTimer = this.fireTimers[0]!;
  }

  private resetPressureTimer(): void {
    if (!this.pressureTimer) return;
    this.pressureTimer.reset({
      delay: BATTLEFIELD_PRESSURE.dropIntervalMs,
      loop: true,
      callback: () => this.applyDefeatPressure(),
    });
    this.pressureTimer.paused = this.paused;
  }

  /** 활성 벽돌 수 (AutoPlay snapshot 등). */
  countActiveBricks(): number {
    return this.runtime.countActiveBricks();
  }

  /** 활성 발사체 수 (성능 오버레이 — GC/풀링 진단). */
  countActiveProjectiles(): number {
    return this.runtime ? this.runtime.countActiveProjectiles() : 0;
  }

  /**
   * 발사체 풀 통계 (성능 오버레이 — 풀링 작동 입증). reused가 created를 크게 상회하면 재사용 정상.
   * idle = 재사용 대기 인스턴스. attach 전엔 0.
   */
  projectilePoolStats(): { created: number; reused: number; idle: number } {
    return this.runtime ? this.runtime.projectileStats() : { created: 0, reused: 0, idle: 0 };
  }

  /** 조준 대상 선택 기준점. 실제 발사도 이 회전축 기준으로 각도를 잡는다. */
  aimLocatorOrigin(): { x: number; y: number } {
    return this.launcherAxis();
  }

  /**
   * 조준선 시작점 — 실제 발사 위치와 같은 포구 tip에 우주선별 muzzle 오프셋을 반영한다.
   * 다중 포문 함선은 한쪽으로 치우친 선을 피하려고 포문 묶음의 중심을 사용한다.
   */
  aimLineOrigin(): { x: number; y: number } {
    const axis = this.launcherAxis();
    const muzzle = this.muzzleCentroid();
    return { x: axis.x + muzzle.offsetX, y: axis.y + muzzle.offsetY };
  }

  getDefeatPressureProgress(): (PressureProgressState & { readonly visible: boolean; readonly threat: PressureThreat; readonly deadlineY: number; readonly threatBottomY: number | null }) {
    const deadlineY = this.defeatDeadlineY();
    if (!this.isDefeatPressureActive()) {
      this.updatePressureDangerTargets(false);
      return {
        visible: false,
        threat: "none",
        deadlineY,
        threatBottomY: null,
        progress: 0,
        remainingMs: 0,
        totalMs: BATTLEFIELD_PRESSURE.dropIntervalMs,
      };
    }
    const total = this.pressureTimer.delay;
    const rowStep = GRID.brickHeight + GRID.vGap;
    const bricks = (this.runtime.bricks.getChildren() as Brick[]).map((brick) => ({
      y: brick.pressureAnchorY,
      height: brick.displayHeight,
      bottom: brick.pressureVisualBottomY,
      active: brick.active,
    }));
    const threatBottomY = resolveLowestActiveBrickBottom(bricks);
    this.updatePressureDangerTargets(true);
    return {
      visible: false,
      threat: resolvePressureThreat(bricks, deadlineY, rowStep, {
        warningRows: BATTLEFIELD_PRESSURE.warningRows,
        dangerRows: BATTLEFIELD_PRESSURE.dangerRows,
      }),
      deadlineY,
      threatBottomY,
      ...resolvePressureProgress(this.pressureTimer.getElapsed(), total),
    };
  }

  /** 전장 비우기 — 던전 진입/복귀·환생·치트 챕터 점프 공용. */
  clearField(): void {
    this.runtime.clear();
    this.aimTargetId = null; // 전장 비움 → stale 조준(같은 좌표 새 벽돌 흡착) 방지.
    this.userTargetId = null; // 수동 조준도 해제 — 새 전장은 자동 조준부터.
    this.invalidateAimCache();
    this.lastTargetMarkerUpdateAt = Number.NEGATIVE_INFINITY;
  }

  private applyDefeatPressure(): void {
    if (this.paused || this.gameOverTriggered) return;
    if (this.deps.isDefeatPressureEnabled && !this.deps.isDefeatPressureEnabled()) return;
    if (this.isContinuousPressureWorld()) return;
    const bricks = (this.runtime.bricks.getChildren() as Brick[]).filter((brick) => brick.active);
    if (bricks.length === 0) return;

    const rowStep = GRID.brickHeight + GRID.vGap;
    const dropDistance = resolveBrickDropDistance(rowStep, BATTLEFIELD_PRESSURE.dropRows);
    if (dropDistance <= 0) return;
    const motion = { dx: 0, dy: dropDistance };

    const finalSamples = bricks.map((brick) => ({
      y: brick.pressureAnchorY + motion.dy,
      height: brick.displayHeight,
      bottom: brick.pressureVisualBottomY + motion.dy,
      active: brick.active,
    }));
    for (const brick of bricks) brick.playPressureAdvance(motion.dx, motion.dy);
    if (
      hasBrickReachedLauncher(
        finalSamples,
        this.defeatDeadlineY(),
      )
    ) {
      this.triggerGameOver();
    }
  }

  private applyContinuousDefeatPressure(deltaMs: number): void {
    if (!this.isContinuousPressureWorld() || !this.isDefeatPressureActive()) return;
    const rowStep = GRID.brickHeight + GRID.vGap;
    const deltaY = resolveContinuousPressureDistance(
      rowStep,
      BATTLEFIELD_PRESSURE.dropRows,
      BATTLEFIELD_PRESSURE.dropIntervalMs,
      deltaMs,
    );
    if (deltaY <= 0) return;
    const bricks = (this.runtime.bricks.getChildren() as Brick[]).filter((brick) => brick.active);
    if (bricks.length === 0) return;
    const finalSamples = bricks.map((brick) => ({
      y: brick.pressureAnchorY + deltaY,
      height: brick.displayHeight,
      bottom: brick.pressureVisualBottomY + deltaY,
      active: brick.active,
    }));
    for (const brick of bricks) brick.translateContentY(deltaY);
    if (hasBrickReachedLauncher(finalSamples, this.defeatDeadlineY())) this.triggerGameOver();
  }

  private isContinuousPressureWorld(): boolean {
    return true;
  }

  private triggerGameOver(): void {
    if (this.gameOverTriggered) return;
    this.gameOverTriggered = true;
    this.setPaused(true);
    this.deps.onGameOver();
  }

  private isDefeatPressureActive(): boolean {
    if (!this.pressureTimer || !this.runtime) return false;
    if (this.paused || this.gameOverTriggered) return false;
    if (this.deps.isDefeatPressureEnabled && !this.deps.isDefeatPressureEnabled()) return false;
    if (BATTLEFIELD_PRESSURE.dropIntervalMs <= 0 || BATTLEFIELD_PRESSURE.dropRows <= 0) return false;
    return this.runtime.countActiveBricks() > 0;
  }

  private updatePressureDangerTargets(active: boolean): void {
    if (!this.runtime) return;
    const rowStep = GRID.brickHeight + GRID.vGap;
    const dropDistance = active
      ? resolveBrickDropDistance(rowStep, BATTLEFIELD_PRESSURE.dropRows)
      : 0;
    const deadlineY = this.defeatDeadlineY();
    const bricks = this.runtime.bricks.getChildren() as Brick[];
    const samples = bricks.map((brick) => ({
      y: brick.pressureAnchorY,
      height: brick.displayHeight,
      bottom: brick.pressureVisualBottomY,
      active: brick.active,
    }));
    const dangerBottom = active
      ? resolveLowestPressureDangerBottom(samples, deadlineY, dropDistance)
      : null;
    for (let i = 0; i < bricks.length; i++) {
      const sampleBottom = pressureBrickBottom(samples[i]);
      bricks[i].setPressureDangerTarget(
        samples[i].active !== false &&
          dangerBottom !== null &&
          Math.abs(sampleBottom - dangerBottom) < 0.5,
      );
    }
  }

  // ── 발사 ────────────────────────────────────────────────────────────────

  private fireFlow(flowIndex: number): void {
    // 첫 발사 1회 퍼널 이벤트 (계정 생애 1회, markOnce dedup) — fire 타이머 최초 발화 = 첫 발사체.
    if (this.deps.ctx.analyticsMilestones().markOnce(AnalyticsEvent.FirstFire)) {
      this.deps.ctx.analytics().log(AnalyticsEvent.FirstFire);
    }
    // 실전 발사는 Math.random으로 chance 게이트 판정 — 모듈 effect의 발동 확률이 발사 1회 단위로 결정된다.
    // 발동한 모듈의 instanceId를 모아 전투탭 그리드 셀에 "발동!" 연출을 띄운다(발동률 100% 모듈 포함).
    // 시작 파이프는 발사가 일어나는 곳이라 매 발사 항상 발동 → startFired=true 무조건 전달(모듈 미발동이어도).
    const procced: BattleProcEvent[] = [];
    const rng = Math.random;
    const result: CombatFlowSpecResult = this.deps.combat.buildCombatFlowSpec(flowIndex, rng, (iid, kind) =>
      procced.push({ instanceId: iid, kind }));
    const spec = result.spec;
    // 숙련도 — 발사 1회당 기여 모듈(중복 제거된 sourceModuleIds)의 activations 누적. 즉시 저장 안 함(PersistScheduler debounce에 위임).
    const mastery = this.deps.ctx.moduleMastery();
    for (const id of spec.sourceModuleIds) mastery.addActivation(id);
    this.deps.onProc(procced, true);
    const bursts = Math.max(1, Math.floor(spec.burstCount));
    this.fireOnce(spec, rng, result.flowIndex); // 1발은 즉시
    // 연사(burst): 동일 패턴을 burstDelayMs 간격으로 추가 발사 (연사 모듈 장착 시 burstCount>1).
    for (let i = 1; i < bursts; i++) {
      this.scene.time.delayedCall(spec.burstDelayMs * i, () => {
        if (this.paused) return;
        this.fireOnce(spec, rng, result.flowIndex);
      });
    }
  }

  /** 한 번의 발사 — factory spawn. 연사(burst)는 fire()가 이를 시간차로 반복 호출한다. */
  private fireOnce(spec: ProjectileSpec, rng: () => number = Math.random, flowIndex?: number): void {
    // 연사(burst) delayedCall이 씬 전환/재로드 후 발화하면 add/tweens/spawn에서 NRE → 씬 생존 가드.
    if (!this.scene.scene.isActive()) return;
    const remainingSlots = this.runtime.remainingProjectileSlots();
    if (remainingSlots <= 0) return;
    const spaceship = this.selectedSpaceship();
    const muzzles = this.projectileMuzzles(flowIndex);
    const projectileSpec = spec;
    const fireCount = resolveSpaceshipProjectileCount(projectileSpec.count, muzzles.length, remainingSlots);
    const projectileTextureKey = spaceship?.projectileAssetKey;
    this.deps.ctx.soundController().play("fire");
    // 조준 각도는 회전축 기준, 발사체 시작점은 우주선 데이터의 포구 좌표 기준.
    const axis = this.launcherAxis();
    const baseAngle = aimBaseAngleRad(axis, this.aimLocator(axis.x, axis.y));
    const shots: Projectile[] = [];
    let remaining = fireCount;
    for (let i = 0; i < muzzles.length && remaining > 0; i++) {
      const muzzle = muzzles[i]!;
      const remainingMuzzles = muzzles.length - i;
      const count = Math.ceil(remaining / remainingMuzzles);
      remaining -= count;
      const origin = { x: axis.x + muzzle.offsetX, y: axis.y + muzzle.offsetY };
      this.emitMuzzleFlash(origin, spaceship?.id);
      shots.push(
        ...this.factory.spawn(
          this.runtime.projectilePool,
          count === projectileSpec.count ? projectileSpec : { ...projectileSpec, count },
          origin,
          baseAngle,
          this.nearestBrick,
          this.playfieldBounds(),
          this.runtime.emitGhost,
          this.runtime.releaseProjectile,
          rng,
          projectileTextureKey,
        ),
      );
    }
    // 발사체는 buildUi 전(loadAndApply 완료 전)에도 생성될 수 있어 ?. — 그 구간은 게임뷰 미렌더라 무해.
    // setContentMask = 본체 마스크(펼침 시 창 밖 삐짐 방지). 잔상 고스트는 emitGhost가 같은 마스크를 별도 적용.
    const geoMask = this.deps.gameviewMask()?.mask;
    for (const p of shots) p.setContentMask(geoMask);
  }

  private emitMuzzleFlash(
    origin: { readonly x: number; readonly y: number },
    spaceshipId: string | undefined,
  ): void {
    if (!this.runtime.allowMuzzleFx()) return;
    const pick = pickSpaceshipMuzzleFlash(spaceshipId, Math.random);
    const fx = muzzleFlash(this.scene, origin.x, origin.y, pick.key, {
      rotation: pick.rotation,
      alpha: pick.alpha,
      startScale: pick.startScale,
      endScale: pick.endScale,
      duration: pick.duration,
    });
    fx.setTint(pick.tint);
    this.runtime.maskFx(fx);
  }

  private playfieldBounds() {
    return scenePlayfieldBounds(this.scene);
  }

  /** 발사 원점(회전축) — 화면 중앙 X + 발사대 Y. fireOnce·updateTargetMarker 공용 SSoT. */
  private launcherAxis(): { x: number; y: number } {
    return { x: this.scene.scale.width / 2, y: sceneLauncherY(this.scene) };
  }

  private defeatDeadlineY(): number {
    return this.deps.defeatDeadlineY?.() ?? this.launcherAxis().y;
  }

  private selectedSpaceship(): ReturnType<typeof spaceshipById> {
    return spaceshipById(this.deps.ctx.spaceship().selectedId);
  }

  private projectileMuzzles(flowIndex?: number): readonly { readonly offsetX: number; readonly offsetY: number }[] {
    const spaceship = this.selectedSpaceship();
    const muzzles = spaceship?.projectileMuzzles.length ? spaceship.projectileMuzzles : [{ offsetX: 0, offsetY: 0 }];
    return spaceshipMuzzlesForFlow(muzzles, flowIndex, this.combatFlowCount());
  }

  private combatFlowCount(): number {
    const grid = this.deps.ctx.grid();
    return Math.max(1, grid.lanes.length);
  }

  private muzzleCentroid(): { offsetX: number; offsetY: number } {
    const muzzles = this.projectileMuzzles();
    let x = 0;
    let y = 0;
    for (const muzzle of muzzles) {
      x += muzzle.offsetX;
      y += muzzle.offsetY;
    }
    return { offsetX: x / muzzles.length, offsetY: y / muzzles.length };
  }

  /** 조준 타겟 마커 갱신 — 매 프레임 현재 조준 블럭 좌표/식별자를 마커에 전달(없으면 숨김, 전환 시 락온 애니). */
  private updateTargetMarker(aimAlreadyResolved = false): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) return;
    const nowMs = this.scene.time.now;
    if (nowMs - this.lastTargetMarkerUpdateAt < TARGET_MARKER_SAMPLE_MS) return;
    this.lastTargetMarkerUpdateAt = nowMs;
    if (!aimAlreadyResolved) {
      const axis = this.launcherAxis();
      this.aimLocator(axis.x, axis.y); // aimTargetId 갱신(멱등 — fireOnce와 동일 결과)
    }
    const target = this.findAimTargetBrick();
    this.aimMarker?.update(
      target
        ? {
            id: `${target.gridX},${target.gridY}`,
            x: target.x,
            y: target.y,
            brickTint: target.tintTopLeft,
            width: target.displayWidth,
            height: target.displayHeight,
          }
        : null,
    );
  }

  /** 사용자 클릭 — 같은 블럭 재클릭이면 수동 조준 해제, 다른 블럭이면 그 블럭으로 강제 조준 전환(toggleTarget 순수). */
  private toggleUserTarget(brick: Brick): void {
    this.userTargetId = toggleTarget(this.userTargetId, `${brick.gridX},${brick.gridY}`);
    this.invalidateAimCache();
    this.lastTargetMarkerUpdateAt = Number.NEGATIVE_INFINITY;
  }

  private findNearestBrick(x: number, y: number): { x: number; y: number } | null {
    return findNearestPoint(x, y, this.runtime.bricks.getChildren() as Brick[]);
  }

  /** 활성 벽돌을 조준 후보로 수집 — id=(gridX,gridY)는 챕터 내 유일(selectAimTarget hysteresis 식별자). */
  private collectAimCandidates(): AimCandidate[] {
    return collectAimCandidates(this.runtime.bricks.getChildren() as Brick[]);
  }

  private findAimTargetBrick(): Brick | null {
    if (this.aimTargetId === null) return null;
    const bricks = this.runtime.bricks.getChildren() as Brick[];
    for (const brick of bricks) {
      if (!brick.active) continue;
      if (`${brick.gridX},${brick.gridY}` === this.aimTargetId) return brick;
    }
    return null;
  }

  applyGameviewOffset(n: number): void {
    this.invalidateAimCache();
    this.gridRuntime.applyGameviewOffset(n);
  }

  applySpaceshipVisualScale(): void {
    this.invalidateAimCache();
    this.gridRuntime.applyEnemyScale();
  }

  reconcileGameviewOffset(): void {
    this.invalidateAimCache();
    this.gridRuntime.reconcileGameviewOffset();
  }

  buildChapterGrid(): void {
    this.gameOverTriggered = false;
    this.aimTargetId = null; // 새 챕터 — 이전 챕터 조준 식별자(같은 좌표) 흡착 방지.
    this.userTargetId = null; // 새 챕터 — 수동 조준 해제.
    this.invalidateAimCache();
    this.lastTargetMarkerUpdateAt = Number.NEGATIVE_INFINITY;
    this.resetPressureTimer(); // 새 챕터 — 하강 progress/다음 하강까지 남은 시간도 새로 시작.
    this.gridRuntime.buildChapterGrid({
      chapter: this.deps.ctx.chapter().currentChapter,
      forceMysteryBricks: this.forceMysteryBricks,
      forceSpecialBricks: this.forceSpecialBricks,
    });
  }

  buildDungeonGrid(index: number): void {
    this.aimTargetId = null;
    this.userTargetId = null;
    this.invalidateAimCache();
    this.lastTargetMarkerUpdateAt = Number.NEGATIVE_INFINITY;
    this.gridRuntime.buildDungeonGrid(index);
  }

  private invalidateAimCache(): void {
    this.lastAimSampleAt = Number.NEGATIVE_INFINITY;
    this.lastAimOriginX = Number.NaN;
    this.lastAimOriginY = Number.NaN;
    this.cachedAimTarget = null;
  }

  transitionChapterGrid(): void {
    this.gridRuntime.transitionChapterGrid(() => this.buildChapterGrid());
  }

  /** 미스터리 100% 강제 토글(디버그 치트) — 플래그 set 후 현재 챕터 즉시 재빌드해 바로 반영. */
  setForceMysteryBricks(enabled: boolean): void {
    this.forceMysteryBricks = enabled;
    this.transitionChapterGrid();
  }

  /** 특수 벽돌(방어막/장갑/재생) 강제 스폰 토글(디버그 치트) — 플래그 set 후 현재 챕터 즉시 재빌드해 바로 반영(QA). */
  setForceSpecialBricks(enabled: boolean): void {
    this.forceSpecialBricks = enabled;
    this.transitionChapterGrid();
  }
}

function resolveLowestActiveBrickBottom(bricks: readonly { readonly y: number; readonly height: number; readonly bottom?: number; readonly active?: boolean }[]): number | null {
  let bottom = Number.NEGATIVE_INFINITY;
  for (const brick of bricks) {
    if (brick.active === false) continue;
    bottom = Math.max(bottom, pressureBrickBottom(brick));
  }
  return Number.isFinite(bottom) ? bottom : null;
}
